import React, { useCallback, useContext, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router';
import { useCriterionClassDiscipline } from '../hooks';
import Card from 'components/Card';
import { DataForm, Input, InputDate } from 'components/formik_inputs';
import { FormikState, FormikValues } from 'formik';
import Button from 'components/common/buttons/Button';
import CriterionInputAsync from 'components/formik_inputs/custom-inputs/CriterionInputAsync';
import ClassInputAsync from 'components/formik_inputs/custom-inputs/ClassInputAsync';
import DisciplinesFromClassInputSelect from 'components/formik_inputs/custom-inputs/ClassFromDisciplineInputSelect';
import ProfessorInputAsync from 'components/formik_inputs/custom-inputs/ProfessorInputAsync';
import moment from 'moment';
import { get } from 'lodash';
import { CriterionClassDisciplineDetail, CriterionClassDisciplineForm } from '../types';
import CriterionClassDisciplineService from '../services'
import { toast } from 'react-toastify';
import ConfirmationModal from 'components/ConfirmationModal';
import { PermissionContext } from 'hoc/withPermission';

const CriterionClassDisciplineFormPage = () => {
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const history = useHistory();
    const { data: criterionClassDiscipline } = useCriterionClassDiscipline(+id)
    const edit = location.pathname.includes('edit');
    const [deleteModal, setDeleteModal] = useState(false);
    const { hasPermission } = useContext(PermissionContext)

    /**
     * Função que retorna data no formato correto para o input, sem aplicar timezone,
     * ou seja, se eu der um `new Date` da forma escolhida, o Javascript não aplica timezone,
     * ou seja, `new Date('2024-10-29')` não se torna `2024-10-29 21:00:00` aqui no Brasil.
     * @param {string} dateString string de data formatada, de preferência, no formato
     * `yyyy-mm-dd`.
     * @returns {string} data formatada advinda do input
     */
    const getDateInstance = useCallback((dateString?: string) => {
        const validDateFormat = /\d{4}-\d{2}-\d{2}/g
        if (!dateString || !validDateFormat.test(dateString)) return null
        const [year, month, day] = dateString.split('-')
        return new Date(+year, +month - 1, +day)
    }, [])

    const assembleFormPayload = useCallback(
        (values: CriterionClassDisciplineDetail)
            : CriterionClassDisciplineForm => ({
                clss: get(values, 'clss.id'),
                teacher: get(values, 'teacher.id', null),
                criterion: get(values, 'criterion.id'),
                discipline: get(values, 'discipline.id'),
                max_activities: get(values, 'max_activities'),
                release_window_start: moment(get(values, 'release_window_start')).format('YYYY-MM-DD'),
                release_window_end: moment(get(values, 'release_window_end')).format('YYYY-MM-DD')
            }), [])

    const getFormInitialValues = useCallback(() => {
        if (edit) {
            return {
                ...criterionClassDiscipline,
                release_window_start: getDateInstance(criterionClassDiscipline?.release_window_start),
                release_window_end: getDateInstance(criterionClassDiscipline?.release_window_end)
            }
        }
        return {
            teacher: null,
            max_activities: 0,
            release_window_start: new Date(),
            release_window_end: new Date(),
            clss: null,
            criterion: null,
            discipline: null
        }
    }, [edit, criterionClassDiscipline])

    const handleSubmit = useCallback(async (values: CriterionClassDisciplineDetail) => {
        const payload = assembleFormPayload(values)
        if (edit) {
            await CriterionClassDisciplineService.update(+id, payload)
            return toast.success('Critério / Turma x atualizado com sucesso!')
        }
        const { id: newId } = await CriterionClassDisciplineService.create(payload)
        toast.success('Critério / Turma x Disciplina cadastrado com sucesso!')
        return history.push(`/criterios-turma-disciplina/${newId}/edit`)
    }, [])

    const getTeacherInputParams = useCallback((values: FormikValues) => {
        const { clss, discipline } = values
        if (clss && discipline) {
            return { clss: clss.id, discipline: discipline.id }
        }
        return {}
    }, [])

    return (
    <>
        <Card title={`${edit ? 'Editar' : 'Cadastrar'} critério x turma/disciplina`}>
            <DataForm
                initialValues={getFormInitialValues()}
                onSubmit={handleSubmit}
                submitOnChange={false}
                autoSetErrors
                enableReinitialize
            >
                {({ values, isSubmitting }: FormikState<CriterionClassDisciplineDetail>) => {
                    return (<>
                        <div className="form-grid-2 mt-2">
                            <CriterionInputAsync
                                name="criterion"
                                placeholder="Selecione um critério..."
                                label="Critério"
                                params={{}}
                                required
                                disabled={isSubmitting}
                            />
                            <ClassInputAsync required defaultOptions name='clss' label='Turma' placeholder="Selecione uma turma" params={{}} />
                        </div>
                        <div className="form-grid-2 mt-2">
                            <DisciplinesFromClassInputSelect
                                required
                                placeholder={!values?.clss ? 'Primeiro, selecione uma turma...' : 'Selecione uma disciplina'}
                                name='discipline'
                                clss={values?.clss}
                                disabled={isSubmitting || !values?.clss}
                            />
                            <Input
                                type='number'
                                name='max_activities'
                                label='Quantidade máxima de atividades por turma disciplina'
                                disabled={isSubmitting}
                            />
                        </div>
                        <div className="form-grid-1 mt-2">
                            <ProfessorInputAsync
                                name='teacher'
                                label='Professor(a)'
                                placeholder={!values?.discipline?.id || !values?.clss?.id ? 'Primeiro, selecione turma e disciplina...' : 'Selecione professor(a)...'}
                                disabled={!values?.discipline?.id || !values?.clss?.id || isSubmitting}
                                defaultOptions
                                noOptionsMessage='Nenhum(a) professor(a) encontrado(a) para a turma/disciplina selecionada.'
                                params={getTeacherInputParams(values)}
                            />

                        </div>
                        <div className="form-grid-2 mt-2">
                            <InputDate
                                yearRange="2024:2030"
                                name='release_window_start'
                                label='Início da vigência de lançamento:'
                                error={undefined}
                                className="w-100"
                                onChange={undefined}
                                required={false}
                            />
                            <InputDate
                                yearRange="2024:2030"
                                name='release_window_end'
                                label='Fim da vigência de lançamento:'
                                error={undefined}
                                onChange={undefined} required={false}
                                className={undefined}
                            />
                        </div>
                        {hasPermission('activities.add_criterionclassdiscipline') && hasPermission('activities.change_criterionclassdiscipline') &&
                            <div className="form-grid-6 mt-2">
                                <Button type="submit">Salvar</Button>
                            </div>}
                    </>)
                }}
            </DataForm>
        </Card>
    </>
    )
}

export default CriterionClassDisciplineFormPage
